// redux/features/sales/saleSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import saleService from "../../../services/saleService";

const initialState = {
  sales:       [],
  sale:        null,
  cumulativeSales: null,
  payments:    [],
  isLoading:   false,
  error:       null,
};

export const createSale = createAsyncThunk("sale/create", async (data, thunkAPI) => {
  try { return await saleService.createSale(data); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const getSales = createAsyncThunk("sale/getAll", async (params, thunkAPI) => {
  try { return await saleService.getSales(params); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const getSingleSale = createAsyncThunk("sale/getSingle", async (id, thunkAPI) => {
  try { return await saleService.getSingleSale(id); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const updateSale = createAsyncThunk("sale/update", async ({ id, data }, thunkAPI) => {
  try { return await saleService.updateSale(id, data); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});


export const voidSale = createAsyncThunk("sale/void", async ({ id, voidReason }, thunkAPI) => {
  try { return await saleService.voidSale(id, { voidReason }); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const addPayment = createAsyncThunk("sale/addPayment", async ({ saleId, data }, thunkAPI) => {
  try { return await saleService.addPayment(saleId, data); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const getSalePayments = createAsyncThunk("sale/getPayments", async (saleId, thunkAPI) => {
  try { return await saleService.getSalePayments(saleId); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});

export const getCumulativeSales = createAsyncThunk("sale/getCumulative", async (params, thunkAPI) => {
  console.log("Dispatching getCumulativeSales with params:")
  try { return await saleService.getCumulativeSales(params); }
  catch (error) { return thunkAPI.rejectWithValue(error.response?.data?.message || error.message); }
});



const saleSlice = createSlice({
  name: "sale",
  initialState,
  reducers: {
    resetSaleState: (state) => {
      state.isLoading = false;
      state.error     = null;
      state.sale      = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSale.pending,   (state) => { state.isLoading = true; })
      .addCase(createSale.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sales.unshift(action.payload);
        toast.success("Sale created successfully");
      })
      .addCase(createSale.rejected,  (state, action) => {
        state.isLoading = false; state.error = action.payload;
        toast.error(action.payload);
      })
      .addCase(getSales.pending,     (state) => { state.isLoading = true; })
      .addCase(getSales.fulfilled, (state, action) => {
    state.isLoading = false;
        console.log("Sales fetched from API:", state.sales.data || state.sales);

    state.sales = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.data || [];  // handle { data: [...] } or plain array
    })
      .addCase(getSales.rejected,    (state, action) => {
        state.isLoading = false; state.error = action.payload;
      })
      .addCase(getCumulativeSales.pending,     (state) => { state.isLoading = true; })
      .addCase(getCumulativeSales.fulfilled, (state, action) => {
        console.log("Cumulative sales fetched from API:", action.payload);
        state.isLoading = false;
        state.cumulativeSales = action.payload;
      })
      .addCase(getCumulativeSales.rejected, (state, action) => {
        console.error("getCumulativeSales error in slice:", action.payload);
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getSingleSale.pending, (state) => { state.isLoading = true; })
      .addCase(getSingleSale.fulfilled, (state, action) => {
        state.isLoading = false; state.sale = action.payload;
      })
        .addCase(getSingleSale.rejected, (state, action) => {
        state.isLoading = false; state.error = action.payload;
        })
        .addCase(voidSale.pending,   (state) => { state.isLoading = true; })
        .addCase(voidSale.fulfilled, (state, action) => {
          state.isLoading = false;
          // Mark as voided in list rather than removing
          state.sales = state.sales.map(s =>
            s._id === action.payload.sale._id ? action.payload.sale : s
          );
          toast.success("Sale voided successfully");
        })
        .addCase(voidSale.rejected, (state, action) => {
          state.isLoading = false;
          toast.error(action.payload);
        })
         .addCase(updateSale.pending, (state) => { state.isLoading = true; })
        .addCase(updateSale.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sales = state.sales.map(s =>
          s._id === action.payload._id ? action.payload : s
        );
        toast.success("Sale updated successfully");
      })
        .addCase(updateSale.rejected, (state, action) => {
        state.isLoading = false; state.error = action.payload;
        toast.error(action.payload);
        })
      .addCase(addPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update sale in list with new payment status
        state.sales = state.sales.map(s =>
          s._id === action.payload.sale._id ? action.payload.sale : s
        );
        toast.success("Payment recorded successfully");
      })
      .addCase(addPayment.rejected, (state, action) => {
        state.isLoading = false; state.error = action.payload;
        toast.error(action.payload);
      })
      .addCase(getSalePayments.fulfilled, (state, action) => {
        state.isLoading = false; state.payments = action.payload;
      });
  },
});

export const { resetSaleState } = saleSlice.actions;
export default saleSlice.reducer;